import { LayoutDashboard, Package, FolderOpen, Activity, Component, Users, ChevronRight, ChevronLeft } from 'lucide-react';
import orbitLogo from '/images/logo.png';
import orbitDarkLogo from '/images/logo-dark.png';
import faviconLogo from '/images/favicon.png';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from 'next-themes';

// `requiredPermission` gates the nav item on the active workgroup's resolved
// role permissions (see AuthContext.hasPermission); omit it for items every
// workgroup member should see regardless of role.
const allNavigationItems = [
  { icon: LayoutDashboard, label: "Dashboard", route: "dashboard" },
  { icon: Package, label: "Applications", route: "applications", requiredPermission: { module: 'applications', action: 'read' } },
  { icon: FolderOpen, label: "Projects", route: "projects", requiredPermission: { module: 'projects', action: 'read' } },
  { icon: Activity, label: "Scans", route: "scans", requiredPermission: { module: 'scans', action: 'read' } },
  { icon: Component, label: "Components Hub", route: "components-hub", requiredPermission: { module: 'components', action: 'read' } },
  { icon: Users, label: "Team", route: "team", requiredPermission: { module: 'teams', action: 'read' } },
];

interface SidebarProps {
  currentRoute: string;
  onRouteChange: (route: string) => void;
  isCollapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ currentRoute, onRouteChange, isCollapsed, onToggle }: SidebarProps) {
  const { hasPermission } = useAuth();
  const { theme } = useTheme();

  const navigationItems = allNavigationItems.filter(
    item => !item.requiredPermission || hasPermission(item.requiredPermission.module, item.requiredPermission.action)
  );

  return (
    <div className={`${isCollapsed ? 'hidden sm:flex w-16' : 'w-60'
      } bg-sidebar border-r border-sidebar-border flex flex-col py-2 h-full transition-all duration-300`}>

      {!isCollapsed ? (
        <div className="px-6 mb-6 flex items-center justify-between flex-shrink-0">
          <img
            src={theme === 'dark' ? orbitDarkLogo : orbitLogo}
            alt="ORbiT8"
            className="h-10 lg:h-12 w-auto"
          />

        </div>

      ) : (
        <div className="px-2 mb-6 flex items-center justify-between flex-shrink-0">
          <img
            src={faviconLogo}
            alt="ORbiT8"
            className="h-10 lg:h-10 w-auto"
          />

        </div>
      )}


      <nav className="flex-1 px-3 space-y-1 lg:space-y-2 overflow-y-auto min-h-0">
        {navigationItems.map((item) => {
          const IconComponent = item.icon;
          const isActive = currentRoute === item.route;
          return (
            <button
              key={item.label}
              onClick={() => onRouteChange(item.route)}
              className={`w-full flex items-center ${isCollapsed ? 'justify-center mb-5' : 'space-x-3 px-4 py-3'} rounded-lg text-left transition-all duration-200 ${isActive
                ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-md"
                : "text-sidebar-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar-accent hover:shadow-md"
                }`}
              title={isCollapsed ? item.label : undefined}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${isActive
                ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-lg shadow-sidebar-primary/20"
                : "text-sidebar-foreground"
                }`}>
                <IconComponent className="w-4 h-4" />
              </div>
              {!isCollapsed && <span className="font-medium">{item.label}</span>}
            </button>
          );
        })}
      </nav>
      <div className="px-3 py-4 border-t border-white/10 mt-2 flex-shrink-0 space-y-3">

{/* 
        {!isCollapsed && (
          <div className="space-y-2 px-6 pt-2">
            <a
              href="https://sourcecodecontrol.com/terms-and-conditions/"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs text-sidebar-foreground/70 hover:text-sidebar-foreground hover:underline transition-colors duration-200"
            >
              Terms and Conditions
            </a>
            <a
              href="https://sourcecodecontrol.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs text-sidebar-foreground/70 hover:text-sidebar-foreground hover:underline transition-colors duration-200"
            >
              Privacy Policy
            </a>
            <a
              href="https://sourcecodecontrol.com/general-data-protection-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs text-sidebar-foreground/70 hover:text-sidebar-foreground hover:underline transition-colors duration-200"
            >
              GDPR Policy
            </a>
          </div>
        )} */}

        <button
          onClick={onToggle}
          className={`flex items-end ${isCollapsed ? 'justify-end' : 'justify-end space-x-2'} px-4 py-2 rounded-lg text-sidebar-foreground/70 hover:text-sidebar-accent-foreground hover:bg-sidebar-accent transition-all duration-200`}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              {!isCollapsed && <span className="font-medium text-sm"></span>}
            </>
          )}
        </button>
      </div>
    </div>
  );
}